import { useState, useRef } from "react";
import { useExplorerStore } from "../../store/explorerStore";
import { useUpload } from "../../hooks/useUpload";
import { UploadModal } from "../upload/UploadModal";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Modal";
import type { SortField, UploadPlan, ResolvedUploadFile } from "@titancloud/shared";

interface FileToolbarProps {
  onCreateFolder: (name: string) => void;
  isCreatingFolder?: boolean;
}

export function FileToolbar({ onCreateFolder, isCreatingFolder = false }: FileToolbarProps) {
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [uploadPlan, setUploadPlan] = useState<UploadPlan | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { planFromFileList, executeUpload } = useUpload();
  const { viewMode, sort, order, setViewMode, setSort, currentFolderId, selectionMode, enterSelectionMode } = useExplorerStore();

  const handleCreateFolder = () => {
    if (folderName.trim() && !isCreatingFolder) {
      onCreateFolder(folderName.trim());
      setFolderName("");
      setNewFolderOpen(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sort === field) {
      setSort(field, order === "asc" ? "desc" : "asc");
    } else {
      setSort(field, "desc");
    }
  };

  const SortButton = ({
    field,
    label,
  }: {
    field: SortField;
    label: string;
  }) => (
    <button
      onClick={() => handleSort(field)}
      className={`flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors ${
        sort === field
          ? "bg-gray-700 text-gray-200"
          : "text-gray-500 hover:text-gray-300"
      }`}
    >
      {label}
      {sort === field && (
        <svg className="h-3 w-3" viewBox="0 0 12 12" fill="currentColor">
          {order === "asc" ? (
            <path d="M6 2l4 6H2l4-6z" />
          ) : (
            <path d="M6 10L2 4h8l-4 6z" />
          )}
        </svg>
      )}
    </button>
  );

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadPlan(planFromFileList(files));
    e.target.value = "";
  };

  const handleUploadConfirm = async (resolved: ResolvedUploadFile[]) => {
    setUploadPlan(null);
    await executeUpload(resolved);
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-800 bg-gray-900/50 px-3 py-2 md:px-4">
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileInputChange}
        />

        {/* Upload button */}
        <Button
          size="sm"
          onClick={() => fileInputRef.current?.click()}
        >
          <svg className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor">
            <path d="M.5 9.9a.5.5 0 01.5.5v2.5a1 1 0 001 1h12a1 1 0 001-1v-2.5a.5.5 0 011 0v2.5a2 2 0 01-2 2H2a2 2 0 01-2-2v-2.5a.5.5 0 01.5-.5z"/>
            <path d="M7.646 1.146a.5.5 0 01.708 0l3 3a.5.5 0 01-.708.708L8.5 2.707V11.5a.5.5 0 01-1 0V2.707L5.354 4.854a.5.5 0 11-.708-.708l3-3z"/>
          </svg>
          Upload
        </Button>

        <Button
          variant="secondary"
          size="sm"
          onClick={() => setNewFolderOpen(true)}
        >
          <svg className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor">
            <path d="M.54 3.87L.5 3a2 2 0 012-2h3.19a2 2 0 011.45.63l.06.07a1 1 0 00.72.3H14a2 2 0 012 2v8a2 2 0 01-2 2H2a2 2 0 01-2-1.97l.54-7.13zM7.5 7h-2a.5.5 0 000 1H7v2a.5.5 0 001 0V8h1.5a.5.5 0 000-1h-2z" />
          </svg>
          New Folder
        </Button>

        <div className="ml-auto flex items-center gap-1 rounded-lg border border-gray-700 bg-gray-800 p-1">
          <SortButton field="name" label="Name" />
          <SortButton field="size" label="Size" />
          <SortButton field="date" label="Date" />
        </div>

        {/* Select — mobile only; hidden once selection mode is active (SelectionBar takes over) */}
        {!selectionMode && (
          <button
            onClick={enterSelectionMode}
            className="rounded-lg border border-gray-700 px-2.5 py-1.5 text-xs font-medium text-gray-400 hover:bg-gray-800 hover:text-gray-200 sm:hidden"
          >
            Select
          </button>
        )}

        {/* View toggle */}
        <div className="flex rounded-lg border border-gray-700 bg-gray-800">
          <button
            onClick={() => setViewMode("grid")}
            className={`p-1.5 transition-colors ${
              viewMode === "grid" ? "text-gray-200" : "text-gray-500 hover:text-gray-300"
            }`}
            title="Grid view"
          >
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor">
              <path d="M1 2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1H2a1 1 0 01-1-1V2zm5 0a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1H7a1 1 0 01-1-1V2zm5 0a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1h-2a1 1 0 01-1-1V2zM1 7a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1H2a1 1 0 01-1-1V7zm5 0a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1H7a1 1 0 01-1-1V7zm5 0a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1h-2a1 1 0 01-1-1V7zM1 12a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1H2a1 1 0 01-1-1v-2zm5 0a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1H7a1 1 0 01-1-1v-2zm5 0a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1h-2a1 1 0 01-1-1v-2z" />
            </svg>
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={`p-1.5 transition-colors ${
              viewMode === "list" ? "text-gray-200" : "text-gray-500 hover:text-gray-300"
            }`}
            title="List view"
          >
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor">
              <path fillRule="evenodd" d="M2 4.5A.5.5 0 012.5 4h11a.5.5 0 010 1h-11a.5.5 0 01-.5-.5zm0 4a.5.5 0 01.5-.5h11a.5.5 0 010 1h-11a.5.5 0 01-.5-.5zm0 4a.5.5 0 01.5-.5h11a.5.5 0 010 1h-11a.5.5 0 01-.5-.5z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>

      <Modal
        open={newFolderOpen}
        onClose={() => setNewFolderOpen(false)}
        title="New Folder"
      >
        <div className="space-y-4">
          <input
            type="text"
            placeholder="Folder name"
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
            autoFocus
            className="w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none"
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setNewFolderOpen(false)} disabled={isCreatingFolder}>
              Cancel
            </Button>
            <Button onClick={handleCreateFolder} disabled={!folderName.trim()} loading={isCreatingFolder}>
              Create
            </Button>
          </div>
        </div>
      </Modal>

      {uploadPlan && (
        <UploadModal
          plan={uploadPlan}
          folderId={currentFolderId}
          onConfirm={handleUploadConfirm}
          onCancel={() => setUploadPlan(null)}
        />
      )}
    </>
  );
}
